import React, { useState } from 'react';
import { Collapse } from 'antd';
import { PublicLayout } from '../../components/PublicLayout';
import { PublicPageSeo } from '../../components/PublicPageSeo';
import { RelatedLinks } from '../../components/RelatedLinks';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import { getContent } from '../../content/i18n';
import { useLocale, useLocalizedPath } from '../../hooks/useLocale';

export const HelpPage: React.FC = () => {
  const locale = useLocale();
  const localizedPath = useLocalizedPath();
  const content = getContent(locale);
  const { help } = content;
  const [selectedArticle, setSelectedArticle] = useState<string | null>(null);
  const homePath = locale === 'en' ? '/en' : '/';
  const pagePath = locale === 'en' ? '/en/help' : '/help';

  const categories = Array.from(new Set(help.articles.map((a) => a.category)));
  const article = help.articles.find((a) => a.id === selectedArticle);

  return (
    <>
      <PublicPageSeo
        breadcrumbs={[
          { name: content.common.home, path: homePath },
          { name: help.title, path: pagePath },
        ]}
        faqs={help.faqs}
      />
      <PublicLayout
        breadcrumbs={[
          { label: content.common.home, href: localizedPath('/') },
          { label: help.title },
        ]}
      >
        <section className="py-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{help.title}</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-12 max-w-3xl">{help.subtitle}</p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {locale === 'zh' ? '常见问题' : 'FAQ'}
          </h2>
          <Collapse
            className="mb-12 bg-white dark:bg-gray-900"
            items={help.faqs.map((faq, index) => ({
              key: String(index),
              label: <span className="font-medium text-gray-900 dark:text-white">{faq.question}</span>,
              children: <p className="text-gray-600 dark:text-gray-300">{faq.answer}</p>,
            }))}
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {locale === 'zh' ? '教程文章' : 'Guides'}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-4">
              {categories.map((category) => (
                <div key={category}>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{category}</h3>
                  <ul className="space-y-2">
                    {help.articles
                      .filter((a) => a.category === category)
                      .map((a) => (
                        <li key={a.id}>
                          <button
                            onClick={() => setSelectedArticle(a.id)}
                            className={`text-left text-sm hover:text-primary-600 dark:hover:text-primary-400 ${
                              selectedArticle === a.id
                                ? 'text-primary-600 dark:text-primary-400 font-medium'
                                : 'text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            {a.title}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="lg:col-span-2 p-6 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 min-h-[300px]">
              {article ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{article.title}</h3>
                  <MarkdownRenderer content={article.body} />
                </>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  {locale === 'zh' ? '请从左侧选择一篇教程' : 'Select a guide from the left'}
                </p>
              )}
            </div>
          </div>
        </section>

        <RelatedLinks
          links={[
            { label: locale === 'zh' ? '客户端下载' : 'Download', href: '/download' },
            { label: locale === 'zh' ? 'iOS 配置' : 'iOS Setup', href: '/download/ios' },
            { label: locale === 'zh' ? '套餐价格' : 'Pricing', href: '/pricing' },
          ]}
        />
      </PublicLayout>
    </>
  );
};
